import { createContext, useContext, useEffect, type ReactNode } from "react"

// Module-level active matter ID — accessible outside the React tree
// (e.g., from the ticket drawer in the Header)
let _activeMatterId: string | null = null

export function getActiveMatterId(): string | null {
  return _activeMatterId
}

interface MatterContextValue {
  /** The MongoDB ObjectID of the matter - use this for all API calls */
  matterId: string
  /** The numeric/display ID of the matter - use this for URL building */
  matterUrlId: string
}

const MatterContext = createContext<MatterContextValue | null>(null)

interface MatterProviderProps {
  children: ReactNode
  matterId: string
  matterUrlId: string
}

/**
 * Provides matter IDs to all children.
 * - matterId: MongoDB ObjectID for API calls
 * - matterUrlId: Numeric ID for URL building (avoids URL rewrites on navigation)
 */
export function MatterProvider({ children, matterId, matterUrlId }: MatterProviderProps) {
  useEffect(() => {
    _activeMatterId = matterId
    return () => { _activeMatterId = null }
  }, [matterId])

  return (
    <MatterContext.Provider value={{ matterId, matterUrlId }}>
      {children}
    </MatterContext.Provider>
  )
}

/**
 * Hook to get the matter's MongoDB ObjectID.
 * Use this instead of useParams() in matter detail child components.
 * Pass an override to use a prop value when available, falling back to context.
 * @throws Error if used outside of MatterProvider and no override is provided
 */
export function useMatterId(override?: string): string {
  const context = useContext(MatterContext)
  if (override) return override
  if (!context) {
    throw new Error("useMatterId must be used within a MatterProvider")
  }
  return context.matterId
}

/**
 * Hook to safely get the matter's MongoDB ObjectID.
 * Returns undefined if used outside of MatterProvider (no error thrown).
 */
export function useMatterIdSafe(): string | undefined {
  const context = useContext(MatterContext)
  return context?.matterId
}

/**
 * Hook to get the matter's numeric/display ID for URL building.
 * Use this for internal navigation within the matter page to avoid URL rewrites.
 * @throws Error if used outside of MatterProvider
 */
export function useMatterUrlId(): string {
  const context = useContext(MatterContext)
  if (!context) {
    throw new Error("useMatterUrlId must be used within a MatterProvider")
  }
  return context.matterUrlId
}
